import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

export default function ServiceMiellerie() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const signatureRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [canSubscribe, setCanSubscribe] = useState(false);
  const [adhesion, setAdhesion] = useState(null);
  const [existingService, setExistingService] = useState(null);
  const [amairAddress, setAmairAddress] = useState(null);
  const [step, setStep] = useState(1); // 1: Info, 2: Récapitulatif, 3: Paiement
  const [serviceDisabled, setServiceDisabled] = useState(false);
  
  const [formData, setFormData] = useState({
    typePaiement: 'en_ligne',
    acceptReglement: false,
    signature: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    checkServiceEnabled();
    checkEligibility();
    fetchAmairAddress();
  }, [user, navigate]);

  const checkServiceEnabled = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/service-settings`);
      if (!response.data.miellerieActif) {
        setServiceDisabled(true);
        setError('Ce service est actuellement indisponible.');
      }
    } catch (err) {
      console.error('Erreur vérification service:', err);
    }
  };

  const checkEligibility = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/can-subscribe/miellerie`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCanSubscribe(response.data.canSubscribe);
      setAdhesion(response.data.adhesion);
      setExistingService(response.data.existingService);
      
      if (!response.data.canSubscribe) {
        setError(response.data.reason);
      }
    } catch (err) {
      setError('Erreur lors de la vérification de l\'éligibilité');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAmairAddress = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/amair-address`
      );
      setAmairAddress(response.data);
    } catch (err) {
      console.error('Erreur récupération adresse AMAIR:', err);
    }
  };

  const handleSignature = () => {
    if (signatureRef.current) {
      const signatureData = signatureRef.current.toDataURL();
      setFormData(prev => ({ ...prev, signature: signatureData }));
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setFormData(prev => ({ ...prev, signature: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.acceptReglement) {
      setError('Veuillez accepter le règlement intérieur');
      return;
    }

    if (!formData.signature) {
      setError('Veuillez signer le formulaire');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const token = localStorage.getItem('token');

      // Créer la souscription au service
      const createResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/services`,
        {
          typeService: 'miellerie',
          adhesionId: adhesion._id,
          typePaiement: formData.typePaiement,
          signature: formData.signature,
          acceptationReglement: formData.acceptReglement,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const service = createResponse.data;

      if (formData.typePaiement === 'en_ligne') {
        // Créer une session de paiement Stripe
        const paymentResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/payment/service/create-payment-session/${service._id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Rediriger vers Stripe
        window.location.href = paymentResponse.data.url;
      } else {
        // Paiement par chèque
        navigate('/dashboard?service_created=true');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la souscription');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-500"></div>
      </div>
    );
  }

  // Si service désactivé
  if (serviceDisabled) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Service temporairement indisponible
            </h1>
            <p className="text-gray-600 mb-6">
              Les inscriptions aux services de la miellerie sont actuellement fermées.
              Veuillez réessayer ultérieurement.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-honey-500 hover:bg-honey-600"
            >
              Retour au tableau de bord
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Si déjà souscrit
  if (existingService) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Vous êtes déjà inscrit aux services de la miellerie
            </h1>
            <p className="text-gray-600 mb-6">
              Votre souscription pour l'année {existingService.annee} est{' '}
              {existingService.status === 'actif' ? 'active' : 'en cours de traitement'}.
            </p>
            <button
              onClick={() => navigate('/mes-adhesions')}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-honey-500 hover:bg-honey-600"
            >
              Retour à mes adhésions
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Si non éligible
  if (!canSubscribe) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <ExclamationTriangleIcon className="h-16 w-16 text-honey-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Non éligible aux services de la miellerie
            </h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-x-4">
              <button
                onClick={() => navigate('/adhesion/amair')}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-honey-500 hover:bg-honey-600"
              >
                Adhérer à l'AMAIR
              </button>
              <button
                onClick={() => navigate('/mes-adhesions')}
                className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Retour
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4">
        {/* En-tête */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Services de la miellerie
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            Souscription pour l'année {adhesion?.annee}
          </p>
        </div>

        {/* Indicateur d'étapes */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s
                      ? 'bg-honey-500 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {s}
                </div>
                {s < 2 && (
                  <div
                    className={`w-16 h-1 mx-2 ${
                      step > s ? 'bg-honey-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-2 space-x-12 text-sm text-gray-600">
            <span>Informations</span>
            <span>Récapitulatif & Signature</span>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* Étape 1: Informations */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Message année */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800">
                      Ce service sera actif pour l'année <strong>{adhesion?.annee}</strong>, 
                      correspondant à votre adhésion AMAIR en cours.
                    </p>
                  </div>
                </div>
              </div>

              {/* Tarifs */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Droit d'usage annuel
                </h2>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-700">Droit d'usage {adhesion?.annee}</span>
                    <span className="text-2xl font-bold text-honey-600">25 €</span>
                  </div>
                </div>
              </div>

              {/* Caution */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Chèque de caution
                </h2>
                <div className="bg-honey-50 border border-honey-200 rounded-lg p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-honey-500 mr-3 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-honey-800">
                      <p className="font-medium mb-2">
                        Un chèque de caution de 300 € à l'ordre de l'AMAIR est requis.
                      </p>
                      {amairAddress && (
                        <p className="mb-2">
                          À déposer impérativement lors du premier passage à la miellerie.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tarifs de la miellerie */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Tarifs des prestations (non inclus dans le droit d'usage)
                </h2>
                <div className="bg-gray-50 rounded-lg p-4">
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex justify-between">
                      <span>Extraction des hausses</span>
                      <span className="font-medium">2 € par hausse</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Fabrication de candi</span>
                      <span className="font-medium">2 € par sac de 25 kg de sucre</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Gaufrage de cire</span>
                      <span className="font-medium">2 € par kg de cire brute</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Conditionnement de miel</span>
                      <span className="font-medium">1 € par tranche de 5 kg</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Mode de paiement */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Mode de paiement du droit d'usage
                </h2>
                <div className="space-y-3">
                  <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="typePaiement"
                      value="en_ligne"
                      checked={formData.typePaiement === 'en_ligne'}
                      onChange={(e) => setFormData(prev => ({ ...prev, typePaiement: e.target.value }))}
                      className="h-4 w-4 text-honey-500 focus:ring-honey-500"
                    />
                    <span className="ml-3">
                      <span className="block text-sm font-medium text-gray-900">
                        Paiement en ligne par carte bancaire
                      </span>
                      <span className="block text-xs text-gray-500">
                        Paiement sécurisé via Stripe
                      </span>
                    </span>
                  </label>
                  {/* <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="typePaiement"
                      value="cheque"
                      checked={formData.typePaiement === 'cheque'}
                      onChange={(e) => setFormData(prev => ({ ...prev, typePaiement: e.target.value }))}
                      className="h-4 w-4 text-honey-500 focus:ring-honey-500"
                    />
                    <span className="ml-3">
                      <span className="block text-sm font-medium text-gray-900">
                        Paiement par chèque
                      </span>
                      <span className="block text-xs text-gray-500">
                        À envoyer avec le chèque de caution
                      </span>
                    </span>
                  </label> */}
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setStep(2)}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-honey-500 hover:bg-honey-600"
                >
                  Continuer
                </button>
              </div>
            </div>
          )}

          {/* Étape 2: Récapitulatif et signature */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">
                Récapitulatif de votre souscription
              </h2>

              <div className="bg-gray-50 rounded-lg p-6 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Service</span>
                  <span className="font-medium">Services de la miellerie</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Année</span>
                  <span className="font-medium">{adhesion?.annee}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Droit d'usage</span>
                  <span className="font-medium">25 €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Mode de paiement</span>
                  <span className="font-medium">
                    {formData.typePaiement === 'en_ligne' ? 'Carte bancaire' : 'Chèque'}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-3">
                  <span className="text-gray-600">Caution requise</span>
                  <span className="font-medium text-honey-600">300 € (chèque)</span>
                </div>
              </div>

              {/* Règlement intérieur */}
              <div className="border rounded-lg p-4">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.acceptReglement}
                    onChange={(e) => setFormData(prev => ({ ...prev, acceptReglement: e.target.checked }))}
                    className="mt-1 h-4 w-4 text-honey-500 focus:ring-honey-500 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    J'accepte le{' '}
                    <a
                      href="/documents/AMAIR_Miellerie_Reglement-interieur.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-honey-600 hover:text-honey-700 underline"
                    >
                      règlement intérieur de la miellerie
                    </a>
                    {' '}et m'engage à le respecter.
                  </span>
                </label>
              </div>

              {/* Signature */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Signature numérique *
                </h3>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 bg-white">
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className: 'w-full h-32 border border-gray-200 rounded',
                    }}
                    onEnd={handleSignature}
                  />
                </div>
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Effacer la signature
                  </button>
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <button
                  onClick={() => setStep(1)}
                  className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Retour
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !formData.acceptReglement || !formData.signature}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-honey-500 hover:bg-honey-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Traitement...
                    </>
                  ) : formData.typePaiement === 'en_ligne' ? (
                    'Procéder au paiement'
                  ) : (
                    'Confirmer la souscription'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
